import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { Radius, RadiusPagingRes } from '@/shared/interfaces/erp/configuracion-red/radius';


const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchRadius = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetRadiusParams> = {}) => {
  return useQuery({
    queryKey: ['radius-red', ...Object.values(params ?? {})],
    queryFn: () => getAllRadius(params),
    enabled: enabled,
  });
};

export const useGetRadius = (id: string) => {
  return useQuery({
    queryKey: ['radius-red', id],
    queryFn: () => getRadius(id),
  });
};

export const useCreateRadius = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createRadius,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['radius-red'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Radius creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateRadius = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateRadius,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['radius-red'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Radius actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteRadius = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRadius,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['radius-red'] });
    },
    onError: () => { },
  });
};

///* axios
export type GetRadiusParams = Partial<Radius> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateRadiusParams = Omit<Radius, 'id_radius'>;
export type UpdateRadiusParams = {
  id: string;
  data: Partial<CreateRadiusParams>;
};

export const getAllRadius = async (params?: GetRadiusParams) => {
  const stateParams = { ...params };

  // filterByState
  // if (stateParams.filterByState === false && stateParams.estado === undefined) {
  //   delete stateParams.estado;
  // } else if (stateParams.filterByState !== false) {
  //   stateParams.estado = true;
  // }
  // delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<RadiusPagingRes>(`${urlBackend}api/v1/radius/?${queryParams}`, true);
};

export const getRadius = async (id: string) => {
  try {
    return await get<Radius>(`${urlBackend}api/v1/radius/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createRadius = async (data: CreateRadiusParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Radius>(`${urlBackend}api/v1/radius/`, data, true);
};

export const updateRadius = async ({ id, data }: UpdateRadiusParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Radius>(`${urlBackend}api/v1/radius/${id}/`, data, true);
};

export const deleteRadius = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<Radius>(`${urlBackend}api/v1/radius/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
